import * as React from "react";

import type { CollectionItem } from "./context";

/**
 * Order the tabs the way a user sees them, not the way React mounted them.
 *
 * ── WHY DOM POSITION AND NOT ARRAY ORDER ──────────────────────────────────────────────────
 *
 * Tabs render through portals into host elements the list moves between slots, so registration
 * order reflects mounting, not layout. Arrow-key navigation has to follow what is on screen —
 * `compareDocumentPosition` is the only source that stays true after a tab moves into the
 * overflow menu and back.
 *
 * An explicit `order` wins over DOM position when both tabs have one, because a tab inside the
 * open overflow menu is in a portal elsewhere in the document and would otherwise sort to the
 * end of everything.
 */
function sortByDomPosition(items: CollectionItem[]): CollectionItem[] {
  const indexed = items.map((item, index) => [index, item] as const);
  let changed = false;

  const sorted = [...indexed].sort(([aIndex, a], [bIndex, b]) => {
    if (a.order != null && a.order >= 0 && b.order != null && b.order >= 0 && a.order !== b.order) {
      if ((a.order < b.order && aIndex > bIndex) || (a.order > b.order && aIndex < bIndex)) {
        changed = true;
      }
      return a.order - b.order;
    }
    if (a.element === b.element || !a.element || !b.element) return 0;
    const position = a.element.compareDocumentPosition(b.element);
    // A disconnected node has no position; leaving it where it is beats inventing an order.
    if (position & Node.DOCUMENT_POSITION_DISCONNECTED) return 0;
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      if (aIndex > bIndex) changed = true;
      return -1;
    }
    if (aIndex < bIndex) changed = true;
    return 1;
  });

  // The SAME array back when nothing moved. Every consumer memoises on identity, so returning a
  // fresh array on every sort would invalidate all of them on every render.
  return changed ? sorted.map(([, item]) => item) : items;
}

/**
 * The registry arrow keys navigate.
 *
 * Wrapping is unconditional here — `getNext` past the last tab returns the first. That matches
 * the ARIA tabs pattern, where a tablist is a loop.
 */
export function useCollection() {
  const itemsRef = React.useRef<CollectionItem[]>([]);
  const itemMap = React.useRef(new Map<string, CollectionItem>());
  const removedItems = React.useRef(new Map<string, CollectionItem & { staleIndex: number }>());
  const [removalVersion, setRemovalVersion] = React.useState(0);
  const rafId = React.useRef<number | null>(null);

  const getOrderedItems = React.useCallback(
    () => sortByDomPosition(Array.from(itemMap.current.values())),
    [],
  );

  /** Only tabs a user can actually reach: those on screen with an element to focus. */
  const getNavigableItems = React.useCallback(
    () => getOrderedItems().filter((item) => item.location !== "hidden" && !!item.element),
    [getOrderedItems],
  );

  const sortItems = React.useCallback(() => {
    itemsRef.current = getOrderedItems();
  }, [getOrderedItems]);

  const scheduleSort = React.useCallback(() => {
    // Coalesced into one frame: mounting ten tabs would otherwise sort ten times, and each sort
    // reads layout.
    if (typeof requestAnimationFrame !== "function") {
      sortItems();
      return;
    }
    if (rafId.current != null) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      sortItems();
    });
  }, [sortItems]);

  React.useEffect(
    () => () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
    },
    [],
  );

  const registerItem = React.useCallback(
    (item: CollectionItem) => {
      itemMap.current.set(item.id, item);
      removedItems.current.delete(item.id);
      scheduleSort();
      return () => {
        // The index is captured BEFORE removal so a caller recovering focus knows where the tab
        // used to be — afterwards that position is gone and "the next tab" is unanswerable.
        const current = getOrderedItems();
        const removed = itemMap.current.get(item.id) ?? item;
        removedItems.current.set(item.id, {
          ...removed,
          staleIndex: current.findIndex(({ id }) => id === item.id),
        });
        itemsRef.current = current.filter(({ id }) => id !== item.id);
        itemMap.current.delete(item.id);
        setRemovalVersion((version) => version + 1);
      };
    },
    [getOrderedItems, scheduleSort],
  );

  const updateItem = React.useCallback(
    (id: string, updates: Partial<CollectionItem>) => {
      const current = itemMap.current.get(id);
      if (!current) return;
      let changed = false;
      const next = { ...current };
      for (const [key, value] of Object.entries(updates)) {
        const typedKey = key as keyof CollectionItem;
        if (next[typedKey] !== value) {
          changed = true;
          (next as Record<string, unknown>)[key] = value;
        }
      }
      // Bailing out when nothing moved is what stops a re-render loop: every tab calls this from
      // a layout effect on every render.
      if (!changed) return;
      itemMap.current.set(id, next);
      itemsRef.current = itemsRef.current.map((item) => (item.id === id ? next : item));
      scheduleSort();
    },
    [scheduleSort],
  );

  return {
    getFirst: React.useCallback(() => getNavigableItems()[0] ?? null, [getNavigableItems]),
    getIndex: React.useCallback(
      (id?: string) => getNavigableItems().findIndex((item) => item.id === id),
      [getNavigableItems],
    ),
    getLast: React.useCallback(() => {
      const items = getNavigableItems();
      return items[items.length - 1] ?? null;
    }, [getNavigableItems]),
    getNext: React.useCallback(
      (id?: string) => {
        const items = getNavigableItems();
        if (!items.length) return null;
        const index = items.findIndex((item) => item.id === id);
        if (index === -1) return items[0] ?? null;
        return items[(index + 1) % items.length] ?? null;
      },
      [getNavigableItems],
    ),
    getPrevious: React.useCallback(
      (id?: string) => {
        const items = getNavigableItems();
        if (!items.length) return null;
        const index = items.findIndex((item) => item.id === id);
        if (index === -1) return items[items.length - 1] ?? null;
        return items[(index - 1 + items.length) % items.length] ?? null;
      },
      [getNavigableItems],
    ),
    getRemovedItems: React.useCallback(() => {
      const items = new Map(removedItems.current);
      // DRAINED on read: these describe one removal event, and replaying them later would
      // recover focus to a tab that has been gone for minutes.
      removedItems.current.clear();
      return items;
    }, []),
    item: React.useCallback((id?: string) => {
      if (!id) return null;
      let found = itemMap.current.get(id);
      if (!found) {
        // The array is the fallback because a tab mid-move is briefly out of the map.
        found = itemsRef.current.find((candidate) => candidate.id === id);
        if (found) itemMap.current.set(found.id, found);
      }
      return found ?? null;
    }, []),
    itemAt: React.useCallback(
      (index: number) => getNavigableItems()[index] ?? null,
      [getNavigableItems],
    ),
    registerItem,
    removalVersion,
    sortItems,
    updateItem,
  };
}
